import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { loadVerbsCsv } from './utils/loadVerbsCsv';
import { conjugatePastGeneric_Salim } from './utils/arPastGeneric_salim';
import { conjugatePresentFormI_Salim_fromRow } from './utils/arPresentFormI_salim';
import { conjugatePresentFormII_Salim_fromRow } from './utils/arPresentFormII_salim';
import { conjugatePresentFormIII_Salim_fromRow } from './utils/arPresentFormIII_salim';
import { conjugatePresentFormIV_Salim_fromRow } from './utils/arPresentFormIV_salim';
import { buildParticiplesFromRoot } from './utils/participlesFormI_salim';
import { buildParticiplesFromRowNonFormI } from './utils/participlesNonFormI';
import { buildImperativeFromRow } from './utils/imperativeFormI_salim';
import { buildImperativeFromRowII } from './utils/imperativeFormII_salim';
import { buildImperativeFromRowIII } from './utils/imperativeFormIII_salim';
import { buildImperativeFromRowIV } from './utils/imperativeFormIV_salim';
import { buildGeneratedMasdar } from './utils/masdarPatterns';
import html2canvas from 'html2canvas';
import alarmClockIcon from './imgs/alarm-clock.svg';
import './VerbTenseGame.css';

const BASE_FORMS = [
  { id: '\u05e2\u05d1\u05e8', name: '\u05e2\u05d1\u05e8' },
  { id: '\u05d4\u05d5\u05d5\u05d4-\u05e2\u05ea\u05d9\u05d3', name: '\u05d4\u05d5\u05d5\u05d4-\u05e2\u05ea\u05d9\u05d3' },
  { id: '\u05e6\u05d9\u05d5\u05d5\u05d9', name: '\u05e6\u05d9\u05d5\u05d5\u05d9' },
  { id: '\u05d1\u05d9\u05e0\u05d5\u05e0\u05d9 \u05e4\u05d5\u05e2\u05dc', name: '\u05d1\u05d9\u05e0\u05d5\u05e0\u05d9 \u05e4\u05d5\u05e2\u05dc' },
  { id: '\u05d1\u05d9\u05e0\u05d5\u05e0\u05d9 \u05e4\u05e2\u05d5\u05dc', name: '\u05d1\u05d9\u05e0\u05d5\u05e0\u05d9 \u05e4\u05e2\u05d5\u05dc' }
];
const MASDAR_FORM = { id: '\u05de\u05e6\u05d3\u05e8', name: '\u05de\u05e6\u05d3\u05e8' };
const FORM_ICON = alarmClockIcon;

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const getForms = (includeMasdar) => (
  includeMasdar
    ? [BASE_FORMS[0], BASE_FORMS[1], BASE_FORMS[2], BASE_FORMS[3], MASDAR_FORM, BASE_FORMS[4]]
    : BASE_FORMS
);

const stripArabicDiacritics = (value) =>
  String(value || '')
    .replace(/[\u064B-\u065F\u0670\u06D6-\u06ED\u0640]/g, '')
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, '')
    .trim();

const normalizeSurfaceForm = (value) =>
  String(value || '')
    .normalize('NFC')
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, '')
    .trim();

const getMasdarPatternId = (value) => {
  const clean = stripArabicDiacritics(value);
  if (!clean) return null;
  if (clean.startsWith('\u0645') && clean.endsWith('\u0629')) return 'mufa3ala';
  if (clean.length === 4) return 'fi3al';
  return null;
};

const isMufa3alaMasdar = (value) => {
  return getMasdarPatternId(value) === 'mufa3ala';
};

const getMasdarOptionsFromRow = (row) => {
  const seen = new Set();

  return [row?.makor1, row?.makor2]
    .map((value) => String(value || '').replace(/\u00A0/g, ' ').trim())
    .filter((value) => {
      const key = normalizeSurfaceForm(value);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const getMasdarFromCsv = (row) => {
  return getMasdarOptionsFromRow(row)[0] || null;
};

const getRandomMasdarFromCsv = (row) => {
  const options = getMasdarOptionsFromRow(row);
  if (options.length === 0) return null;
  return options[Math.floor(Math.random() * options.length)];
};

const getPassiveFeminineMasdarMatch = (row, participlesBuilder, participles = null) => {
  const resolvedParticiples = participles || participlesBuilder(row);
  const passiveFeminine = resolvedParticiples?.passive?.feminine || null;
  if (!passiveFeminine) return null;

  const benoniPaulAllowed = row['benoni_paul_allowed'] || '';
  if (!isAllowed(benoniPaulAllowed, 'feminine')) {
    return null;
  }

  const matchingMasdar = getMasdarOptionsFromRow(row).find(
    (masdar) =>
      getMasdarPatternId(masdar) === 'mufa3ala' &&
      normalizeSurfaceForm(masdar) === normalizeSurfaceForm(passiveFeminine)
  );

  if (!matchingMasdar) {
    return null;
  }

  return {
    passiveFeminine,
    masdar: matchingMasdar
  };
};

const getEligibleMasdarEntries = (
  row,
  participlesBuilder,
  { enforceBinyan3Ambiguity = false, requireFi3al = false, participles = null } = {}
) => {
  const ambiguousMatch = enforceBinyan3Ambiguity
    ? getPassiveFeminineMasdarMatch(row, participlesBuilder, participles)
    : null;

  return getMasdarOptionsFromRow(row)
    .map((value) => ({
      value,
      patternId: getMasdarPatternId(value)
    }))
    .filter(({ value, patternId }) => {
      if (!patternId) return false;
      if (requireFi3al && patternId !== 'fi3al') return false;
      if (!enforceBinyan3Ambiguity) return true;
      if (patternId === 'fi3al') return true;
      return Boolean(
        ambiguousMatch &&
        normalizeSurfaceForm(ambiguousMatch.masdar) === normalizeSurfaceForm(value)
      );
    });
};

const getEligiblePassiveGenderOptions = (
  row,
  participlesBuilder,
  { enforceBinyan3Ambiguity = false, requireNonFeminine = false, participles = null } = {}
) => {
  const resolvedParticiples = participles || participlesBuilder(row);
  if (!resolvedParticiples?.passive) return [];

  const benoniPaulAllowed = row['benoni_paul_allowed'] || '';
  const ambiguousMatch = enforceBinyan3Ambiguity
    ? getPassiveFeminineMasdarMatch(row, participlesBuilder, resolvedParticiples)
    : null;

  return GENDER_OPTIONS
    .map((gender) => gender.id)
    .filter((genderId) => {
      if (!isAllowed(benoniPaulAllowed, genderId)) return false;
      if (requireNonFeminine && genderId === 'feminine') return false;
      if (enforceBinyan3Ambiguity && genderId === 'feminine') {
        return Boolean(ambiguousMatch);
      }
      return true;
    });
};

const getAcceptedFormsForQuestion = ({
  binyan,
  targetTense,
  verbData,
  displayedVerb,
  displayedGender,
  participles,
  participlesBuilder
}) => {
  const acceptedForms = [targetTense];

  if (binyan !== '3') {
    return acceptedForms;
  }

  if (targetTense === MASDAR_FORM.id) {
    if (!isMufa3alaMasdar(displayedVerb)) {
      return acceptedForms;
    }

    const benoniPaulAllowed = verbData['benoni_paul_allowed'] || '';
    if (!isAllowed(benoniPaulAllowed, 'feminine')) {
      return acceptedForms;
    }

    const passiveFeminine = participles?.passive?.feminine || participlesBuilder(verbData)?.passive?.feminine;
    if (normalizeSurfaceForm(passiveFeminine) === normalizeSurfaceForm(displayedVerb)) {
      acceptedForms.push(BASE_FORMS[4].id);
    }

    return acceptedForms;
  }

  if (targetTense === BASE_FORMS[4].id && displayedGender === 'feminine') {
    const matchingMasdar = getMasdarOptionsFromRow(verbData).some(
      (masdar) => isMufa3alaMasdar(masdar) && normalizeSurfaceForm(masdar) === normalizeSurfaceForm(displayedVerb)
    );

    if (matchingMasdar) {
      acceptedForms.push(MASDAR_FORM.id);
    }
  }

  return acceptedForms;
};

const PRONOUNS = [
  { arabic: 'أَنَا' },
  { arabic: 'أَنْتَ' },
  { arabic: 'أَنْتِ' },
  { arabic: 'هُوَ' },
  { arabic: 'هِيَ' },
  { arabic: 'نَحْنُ' },
  { arabic: 'أَنْتُمْ' },
  { arabic: 'أَنْتُنَّ' },
  { arabic: 'هُمْ' },
  { arabic: 'هُنَّ' }
];

const GENDER_OPTIONS = [
  { id: 'masculine', nameHeb: 'יחיד' },
  { id: 'feminine', nameHeb: 'יחידה' },
  { id: 'plural', nameHeb: 'רבים' },
  { id: 'pluralFeminine', nameHeb: 'רבות' }
];

const isAllowed = (allowedValue, genderId) => {
  if (!allowedValue || allowedValue.trim() === '') return false;
  
  const genderMap = {
    'masculine': 'sm',
    'feminine': 'sf',
    'plural': 'pm',
    'pluralFeminine': 'pf'
  };
  
  const allowedStr = allowedValue.toLowerCase().trim();
  if (allowedStr === 'all') return true;
  
  const genderCode = genderMap[genderId];
  if (!genderCode) return false;
  
  const allowedCodes = allowedStr.split(',').map(s => s.trim());
  return allowedCodes.includes(genderCode);
};

const DEFAULT_GAME_CONFIG = {
  title: 'מהי הצורה הדקדוקית?',
  summaryTitle: 'מהי הצורה הדקדוקית?',
  shareTitle: 'תוצאות מהי הצורה הדקדוקית?',
  shareFileName: 'תוצאות-מהי-הצורה-הדקדוקית-בניין-1.png',
  shareTextLabel: 'מהי הצורה הדקדוקית?',
  csvPath: '/verbs-grade10.csv',
  binyan: '1',
  includeMasdar: false,
  buildMasdar: getMasdarFromCsv
};

const BINYAN_GAME_CONFIGS = {
  '1': {
    allowedGizraPatterns: ['שלמים'],
    presentConjugator: conjugatePresentFormI_Salim_fromRow,
    participlesBuilder: buildParticiplesFromRoot,
    imperativeBuilder: buildImperativeFromRow,
    requiresFutureVowelForImperative: true,
    buildMasdar: getMasdarFromCsv
  },
  '2': {
    allowedGizraPatterns: ['שלמים', 'כפולים', 'ע"ו', 'פ"ו'],
    presentConjugator: conjugatePresentFormII_Salim_fromRow,
    participlesBuilder: buildParticiplesFromRowNonFormI,
    imperativeBuilder: buildImperativeFromRowII,
    requiresFutureVowelForImperative: false,
    buildMasdar: buildGeneratedMasdar,
    shareFileName: 'תוצאות-מהי-הצורה-הדקדוקית-בניין-2.png'
  },
  '3': {
    allowedGizraPatterns: ['שלמים', 'ע"ו', 'פ"ו'],
    presentConjugator: conjugatePresentFormIII_Salim_fromRow,
    participlesBuilder: buildParticiplesFromRowNonFormI,
    imperativeBuilder: buildImperativeFromRowIII,
    requiresFutureVowelForImperative: false,
    buildMasdar: getRandomMasdarFromCsv,
    shareFileName: 'תוצאות-מהי-הצורה-הדקדוקית-בניין-3.png'
  },
  '4': {
    allowedGizraPatterns: ['שלמים'],
    presentConjugator: conjugatePresentFormIV_Salim_fromRow,
    participlesBuilder: buildParticiplesFromRowNonFormI,
    imperativeBuilder: buildImperativeFromRowIV,
    requiresFutureVowelForImperative: false,
    buildMasdar: buildGeneratedMasdar,
    shareFileName: 'תוצאות-מהי-הצורה-הדקדוקית-בניין-4.png'
  }
};

function VerbTenseGame2({ gameConfig = DEFAULT_GAME_CONFIG }) {
  const navigate = useNavigate();
  const { grade } = useParams();
  const location = useLocation();
  const fromBinyan = location.state?.fromBinyan;
  const binyanDefaults = BINYAN_GAME_CONFIGS[gameConfig?.binyan || DEFAULT_GAME_CONFIG.binyan] || BINYAN_GAME_CONFIGS[DEFAULT_GAME_CONFIG.binyan];
  const config = {
    ...DEFAULT_GAME_CONFIG,
    ...binyanDefaults,
    ...gameConfig
  };
  const {
    allowedGizraPatterns,
    binyan: targetBinyan,
    buildMasdar,
    csvPath,
    includeMasdar,
    imperativeBuilder,
    participlesBuilder,
    presentConjugator,
    requiresFutureVowelForImperative,
    shareFileName,
    shareTextLabel,
    shareTitle,
    summaryTitle,
    title
  } = config;
  const forms = getForms(includeMasdar);
  const enforceBinyan3MasdarRules = targetBinyan === '3' && includeMasdar;
  
  const getBackUrl = () => {
    if (fromBinyan) {
      return `/grade/${grade}/verbs/binyan/${fromBinyan}`;
    }
    return `/grade/${grade}/verbs`;
  };
  
  const [gameVerbs, setGameVerbs] = useState([]);
  const [questionPlans, setQuestionPlans] = useState([]);
  const [currentVerbIndex, setCurrentVerbIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedLock, setSelectedLock] = useState(null);
  const [gameHistory, setGameHistory] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [draggedKey, setDraggedKey] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [draggedElement, setDraggedElement] = useState(null);
  const [hoveredLock, setHoveredLock] = useState(null);
  const [showResultsPopup, setShowResultsPopup] = useState(false);
  const [currentAttempts, setCurrentAttempts] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const resultsSummaryRef = useRef(null);
  
  const audioContextRef = useRef(null);
  const usedPoelGenders = useRef([]);
  const usedPaulGenders = useRef([]);
  
  const initAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
  };
  
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const generateQuestion = useCallback((verbs, plans, index) => {
    if (index >= verbs.length) {
      setShowResults(true);
      return;
    }
    
    const verbData = verbs[index];
    const questionPlan = plans[index] || {};
    const targetTense = questionPlan.targetTense;
    
    let conjugatedVerb;
    let conjugations;
    let participles = null;
    let displayedGender = null;
    
    if (targetTense === BASE_FORMS[0].id) {
      conjugations = conjugatePastGeneric_Salim(verbData['פועל']);
      if (conjugations) {
        const availablePronouns = PRONOUNS.filter(p => conjugations[p.arabic]);
        const randomPronoun = availablePronouns[Math.floor(Math.random() * availablePronouns.length)];
        conjugatedVerb = conjugations[randomPronoun.arabic];
      }
    } else if (targetTense === BASE_FORMS[1].id) {
      conjugations = presentConjugator(verbData);
      if (conjugations) {
        const availablePronouns = PRONOUNS.filter(p => conjugations[p.arabic]);
        const randomPronoun = availablePronouns[Math.floor(Math.random() * availablePronouns.length)];
        conjugatedVerb = conjugations[randomPronoun.arabic];
      }
    } else if (targetTense === BASE_FORMS[2].id) {
      const imperativeForms = imperativeBuilder(verbData);
      if (imperativeForms) {
        const imperativeKeys = ['you_ms', 'you_fs', 'you_mp', 'you_fp'];
        const randomKey = imperativeKeys[Math.floor(Math.random() * imperativeKeys.length)];
        conjugatedVerb = imperativeForms[randomKey];
      }
    } else if (targetTense === BASE_FORMS[3].id || targetTense === BASE_FORMS[4].id) {
      participles = participlesBuilder(verbData);
      if (participles) {
        const benoniPoelAllowed = verbData['benoni_poel_allowed'] || '';
        
        const availableGenders = targetTense === BASE_FORMS[3].id
          ? GENDER_OPTIONS
              .map((gender) => gender.id)
              .filter((genderId) => isAllowed(benoniPoelAllowed, genderId))
          : getEligiblePassiveGenderOptions(verbData, participlesBuilder, {
              enforceBinyan3Ambiguity: enforceBinyan3MasdarRules,
              requireNonFeminine: Boolean(questionPlan.requireNonFemininePassive),
              participles
            });
        
        if (availableGenders.length > 0) {
          let unusedGenders;
          if (targetTense === BASE_FORMS[3].id) {
            unusedGenders = availableGenders.filter(g => !usedPoelGenders.current.includes(g));
          } else {
            unusedGenders = availableGenders.filter(g => !usedPaulGenders.current.includes(g));
          }
          
          const gendersToChooseFrom = unusedGenders.length > 0 ? unusedGenders : availableGenders;
          const randomGender = gendersToChooseFrom[Math.floor(Math.random() * gendersToChooseFrom.length)];
          displayedGender = randomGender;
          
          if (targetTense === BASE_FORMS[3].id) {
            conjugatedVerb = participles.active[randomGender];
            usedPoelGenders.current.push(randomGender);
          } else {
            conjugatedVerb = participles.passive[randomGender];
            usedPaulGenders.current.push(randomGender);
          }
        }
      }
    } else if (targetTense === MASDAR_FORM.id) {
      if (enforceBinyan3MasdarRules) {
        const eligibleMasdars = getEligibleMasdarEntries(verbData, participlesBuilder, {
          enforceBinyan3Ambiguity: true,
          requireFi3al: Boolean(questionPlan.requireFi3alMasdar)
        });
        if (eligibleMasdars.length > 0) {
          conjugatedVerb = eligibleMasdars[Math.floor(Math.random() * eligibleMasdars.length)].value;
        }
      } else {
        const masdar = typeof buildMasdar === 'function' ? buildMasdar(verbData) : null;
        if (masdar) {
          conjugatedVerb = masdar;
        }
      }
    }
    
    if (!conjugatedVerb) {
      console.error('Failed to generate verb for:', targetTense, verbData);
      setLoadError('לא נמצאו שאלות מתאימות למשחק הזה.');
      return;
    }
    
    const acceptedForms = getAcceptedFormsForQuestion({
      binyan: targetBinyan,
      targetTense,
      verbData,
      displayedVerb: conjugatedVerb,
      displayedGender,
      participles,
      participlesBuilder
    });

    setCurrentQuestion({
      verb: conjugatedVerb,
      verbData: verbData,
      correctForm: targetTense,
      acceptedForms,
      displayCorrectForm: acceptedForms.join(' / ')
    });
    
    setFeedback(null);
    setSelectedLock(null);
    setDraggedKey(false);
    setIsDragging(false);
    setDraggedElement(null);
    setHoveredLock(null);
    setCurrentAttempts(0);
    setHasError(false);
  }, [buildMasdar, enforceBinyan3MasdarRules, imperativeBuilder, participlesBuilder, presentConjugator, targetBinyan]);

  const createBalancedTenseSequence = useCallback(() => {
    const activeForms = getForms(includeMasdar);
    const createTargetCounts = () => {
      const counts = Object.fromEntries(activeForms.map(form => [form.id, includeMasdar ? 1 : 2]));

      if (includeMasdar) {
        for (let i = 0; i < 4; i += 1) {
          const availableForms = activeForms.filter(form => counts[form.id] < 2);
          if (availableForms.length === 0) break;
          const chosenForm = availableForms[Math.floor(Math.random() * availableForms.length)];
          counts[chosenForm.id] += 1;
        }
      }

      return counts;
    };

    const countAdjacentRepeatedForms = (sequence) =>
      sequence.reduce((count, tense, index) => (
        index > 0 && tense === sequence[index - 1] ? count + 1 : count
      ), 0);

    const buildLimitedAdjacentFallback = (counts) => {
      const fallback = Object.entries(counts).flatMap(([tense, count]) => Array(count).fill(tense));

      for (let attempt = 0; attempt < 100; attempt += 1) {
        const candidate = shuffleArray(fallback);
        if (countAdjacentRepeatedForms(candidate) <= 1) {
          return candidate;
        }
      }

      const remainingCounts = { ...counts };
      const arranged = [];
      let adjacentPairs = 0;

      while (arranged.length < fallback.length) {
        const lastTense = arranged[arranged.length - 1];
        const availableTenses = Object.keys(remainingCounts)
          .filter(tense => remainingCounts[tense] > 0)
          .sort((a, b) => remainingCounts[b] - remainingCounts[a]);
        const chosenTense = availableTenses.find(tense => tense !== lastTense) || availableTenses[0];

        if (!chosenTense) break;
        if (chosenTense === lastTense) {
          adjacentPairs += 1;
        }

        arranged.push(chosenTense);
        remainingCounts[chosenTense] -= 1;
      }

      return adjacentPairs <= 1 && arranged.length === fallback.length ? arranged : fallback;
    };

    let attempts = 0;
    const maxAttempts = 100;
    
    while (attempts < maxAttempts) {
      attempts++;
      const result = [];
      const tenseCount = createTargetCounts();
      
      let lastTense = null;
      let consecutiveCount = 0;
      let failed = false;
      
      while (result.length < 10 && !failed) {
        const availableTenses = Object.keys(tenseCount).filter(t => tenseCount[t] > 0);
        
        if (availableTenses.length === 0) break;
        
        const canChoose = {};
        for (const tense of availableTenses) {
          canChoose[tense] = true;
          if (lastTense === tense && consecutiveCount >= 2) {
            canChoose[tense] = false;
          }
        }
        
        const validChoices = availableTenses.filter(t => canChoose[t]);
        
        if (validChoices.length === 0) {
          failed = true;
          break;
        }
        
        const weights = {};
        let totalWeight = 0;
        validChoices.forEach(tense => {
          weights[tense] = tenseCount[tense];
          totalWeight += tenseCount[tense];
        });
        
        let random = Math.random() * totalWeight;
        let chosenTense = validChoices[0];
        
        for (const tense of validChoices) {
          random -= weights[tense];
          if (random <= 0) {
            chosenTense = tense;
            break;
          }
        }
        
        result.push(chosenTense);
        tenseCount[chosenTense]--;
        
        if (lastTense === chosenTense) {
          consecutiveCount++;
        } else {
          consecutiveCount = 1;
          lastTense = chosenTense;
        }
      }
      
      if (!failed && result.length === 10) {
        if (countAdjacentRepeatedForms(result) > 1) {
          continue;
        }
        return result;
      }
    }
    
    const fallbackCounts = createTargetCounts();
    return buildLimitedAdjacentFallback(fallbackCounts);
  }, [includeMasdar]);

  const createQuestionPlans = useCallback((tenses) => {
    let assignedFi3alMasdar = false;
    let assignedFirstPassive = false;

    return tenses.map((targetTense) => {
      const plan = { targetTense };

      if (!enforceBinyan3MasdarRules) {
        return plan;
      }

      if (targetTense === MASDAR_FORM.id && !assignedFi3alMasdar) {
        plan.requireFi3alMasdar = true;
        assignedFi3alMasdar = true;
      }

      if (targetTense === BASE_FORMS[4].id && !assignedFirstPassive) {
        plan.requireNonFemininePassive = true;
        assignedFirstPassive = true;
      }

      return plan;
    });
  }, [enforceBinyan3MasdarRules]);

  useEffect(() => {
    const loadVerbs = async () => {
      try {
        setLoadError('');
        setIsLoaded(false);
        usedPoelGenders.current = [];
        usedPaulGenders.current = [];
        
        const rows = await loadVerbsCsv(csvPath);
        
        const baseVerbs = rows.filter(r => {
          const binyan = (r['בניין'] || '').trim();
          const gizra = (r['gizra'] || '').trim();
          const tense = (r['זמן'] || '').trim();
          const hasFaVowelFuture = (r['R2_FUTURE'] || r['fa_vowel_future'] || '').trim();
          
          return (binyan === targetBinyan || binyan === `בניין ${targetBinyan}`) && 
                 allowedGizraPatterns.some(pattern => gizra.includes(pattern)) && 
                 tense === BASE_FORMS[0].id &&
                 (!requiresFutureVowelForImperative || hasFaVowelFuture);
        });

        if (baseVerbs.length === 0) {
          setLoadError('לא נמצאו פעלים מתאימים למשחק הזה.');
          return;
        }
        
        const tenseSequence = createBalancedTenseSequence();
        const nextQuestionPlans = createQuestionPlans(tenseSequence);
        
        const selectedVerbs = [];
        for (const plan of nextQuestionPlans) {
          const { targetTense } = plan;
          let availableVerbs = [...baseVerbs];
          
          if (targetTense === BASE_FORMS[4].id) {
            availableVerbs = availableVerbs.filter(v => {
              if (enforceBinyan3MasdarRules) {
                return getEligiblePassiveGenderOptions(v, participlesBuilder, {
                  enforceBinyan3Ambiguity: true,
                  requireNonFeminine: Boolean(plan.requireNonFemininePassive)
                }).length > 0;
              }

              const benoniPaulAllowed = (v['benoni_paul_allowed'] || '').trim();
              return benoniPaulAllowed !== '';
            });
          } else if (targetTense === BASE_FORMS[3].id) {
            availableVerbs = availableVerbs.filter(v => {
              const benoniPoelAllowed = (v['benoni_poel_allowed'] || '').trim();
              return benoniPoelAllowed !== '';
            });
          } else if (targetTense === MASDAR_FORM.id) {
            availableVerbs = availableVerbs.filter(v => {
              if (enforceBinyan3MasdarRules) {
                return getEligibleMasdarEntries(v, participlesBuilder, {
                  enforceBinyan3Ambiguity: true,
                  requireFi3al: Boolean(plan.requireFi3alMasdar)
                }).length > 0;
              }

              const masdar = typeof buildMasdar === 'function' ? buildMasdar(v) : null;
              return Boolean(masdar);
            });
          }
          
          const usedRoots = selectedVerbs.map(v => v['שורש']);
          const unusedVerbs = availableVerbs.filter(v => !usedRoots.includes(v['שורש']));
          
          const poolToUse = unusedVerbs.length > 0 ? unusedVerbs : availableVerbs;
          if (poolToUse.length === 0) {
            setLoadError('לא נמצאו מספיק פעלים מתאימים לכל סוגי השאלות במשחק הזה.');
            return;
          }
          const randomVerb = poolToUse[Math.floor(Math.random() * poolToUse.length)];
          selectedVerbs.push(randomVerb);
        }
        
        setGameVerbs(selectedVerbs);
        setQuestionPlans(nextQuestionPlans);
        generateQuestion(selectedVerbs, nextQuestionPlans, 0);
      } catch (error) {
        console.error('שגיאה בטעינת פעלים:', error);
        setLoadError('שגיאה בטעינת הפעלים למשחק.');
      } finally {
        setIsLoaded(true);
      }
    };
    
    loadVerbs();
  }, [allowedGizraPatterns, buildMasdar, createBalancedTenseSequence, createQuestionPlans, csvPath, enforceBinyan3MasdarRules, generateQuestion, participlesBuilder, requiresFutureVowelForImperative, targetBinyan]);

  const handlePointerStart = (e) => {
    if (feedback === 'correct') return;
    
    e.preventDefault();
    setDraggedKey(true);
    setIsDragging(true);
    setDragStartPos({ x: e.clientX, y: e.clientY });
    setDraggedElement(e.currentTarget);
    
    e.currentTarget.setPointerCapture(e.pointerId);
    e.currentTarget.style.zIndex = '1000';
  };

  const handlePointerMove = (e) => {
    if (!isDragging || !draggedElement) return;
    
    const dx = e.clientX - dragStartPos.x;
    const dy = e.clientY - dragStartPos.y;
    
    draggedElement.style.transform = `translate(${dx}px, ${dy}px)`;
    
    const locks = document.querySelectorAll('.lock');
    let foundHoveredLock = null;
    
    locks.forEach((lock) => {
      const rect = lock.getBoundingClientRect();
      const margin = 30;
      
      if (e.clientX >= rect.left - margin &&
          e.clientX <= rect.right + margin &&
          e.clientY >= rect.top - margin &&
          e.clientY <= rect.bottom + margin) {
        
        const formId = lock.getAttribute('data-form-id');
        if (formId) {
          foundHoveredLock = formId;
        }
      }
    });
    
    setHoveredLock(foundHoveredLock);
  };

  const handlePointerEnd = (e) => {
    if (!isDragging || !draggedElement) return;
    
    draggedElement.style.transform = '';
    draggedElement.style.zIndex = '';
    
    if (hoveredLock) {
      setSelectedLock(hoveredLock);
      checkAnswer(hoveredLock);
    }
    
    setIsDragging(false);
    setDraggedKey(false);
    setDraggedElement(null);
    setHoveredLock(null);
  };

  const checkAnswer = (formId) => {
    const acceptedForms = currentQuestion.acceptedForms || [currentQuestion.correctForm];
    const isCorrect = acceptedForms.includes(formId);
    
    const updatedAttempts = currentAttempts + 1;
    setCurrentAttempts(updatedAttempts);
    
    setFeedback(isCorrect ? 'correct' : 'wrong');
    
    if (isCorrect) {
      playSuccessSound();
      setTimeout(() => {
        saveToHistory(updatedAttempts, formId);
        nextQuestion();
      }, 600);
    } else {
      setHasError(true);
      playErrorSound();
      setTimeout(() => {
        setFeedback(null);
        setSelectedLock(null);
      }, 800);
    }
  };

  const saveToHistory = (finalAttempts, userAnswer) => {
    setGameHistory(prev => [...prev, {
      verb: currentQuestion.verb,
      verbData: currentQuestion.verbData,
      correctForm: currentQuestion.correctForm,
      acceptedForms: currentQuestion.acceptedForms || [currentQuestion.correctForm],
      displayCorrectForm: currentQuestion.displayCorrectForm || currentQuestion.correctForm,
      userAnswer,
      attempts: finalAttempts,
      hadError: hasError
    }]);
  };

  const nextQuestion = () => {
    const nextIndex = currentVerbIndex + 1;
    setCurrentVerbIndex(nextIndex);
    generateQuestion(gameVerbs, questionPlans, nextIndex);
  };

  const playSuccessSound = () => {
    try {
      initAudioContext();
      if (!audioContextRef.current) return;
      const audioContext = audioContextRef.current;
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 523.25;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  const playErrorSound = () => {
    try {
      initAudioContext();
      if (!audioContextRef.current) return;
      const audioContext = audioContextRef.current;
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 200;
      oscillator.type = 'sawtooth';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  const handleBackToGames = () => {
    navigate(getBackUrl());
  };

  const generateResultsImage = async () => {
    if (!resultsSummaryRef.current) return null;
    const canvas = await html2canvas(resultsSummaryRef.current, {
      backgroundColor: '#5eb9c5',
      scale: 2,
    });
    return canvas.toDataURL('image/png');
  };

  const dataURLtoBlob = (dataURL) => {
    const parts = dataURL.split(',');
    const mime = parts[0].match(/:(.*?);/)[1];
    const bstr = atob(parts[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    return new Blob([u8arr], { type: mime });
  };

  const shareViaWhatsApp = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;

    const blob = dataURLtoBlob(imageData);
    const fileName = shareFileName;
    const file = new File([blob], fileName, { type: 'image/png' });

    if (navigator.share && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: shareTitle,
          text: `${shareTextLabel}\nהשגתי ${gameHistory.filter(h => !h.hadError).length}/${gameHistory.length}!`,
          files: [file],
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('שגיאה בשיתוף:', err);
        }
      }
    } else {
      const link = document.createElement('a');
      link.download = fileName;
      link.href = URL.createObjectURL(blob);
      link.click();
    }
    setShowResultsPopup(false);
  };

  const shareViaEmail = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;
    const fileName = shareFileName;
    const link = document.createElement('a');
    link.download = fileName;
    link.href = imageData;
    link.click();
    setShowResultsPopup(false);
  };

  if (showResults) {
    const correctCount = gameHistory.filter(h => !h.hadError).length;

    return (
      <div className="App">
        <header className="App-header">
          <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
            <button onClick={handleBackToGames} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
              חזרה למשחקי הפועל ↩
            </button>
            <h1 className="game-title-with-back" style={{ margin: 0 }}>תוצאות המשחק</h1>
          </div>
        </header>
        <main className="game-container results-screen">
          <div className="words-summary" ref={resultsSummaryRef}>
            <h3>{summaryTitle}</h3>
            <div className="final-score-display">
              ניקוד: {correctCount}/{gameHistory.length}
            </div>
            <br/>
            <div className="words-list">
              {gameHistory.map((item, idx) => (
                <div key={idx} className="word-item">
                  <div className="word-pair">
                    <span className="arabic-word-summary">{item.verb}</span>
                    <span className="separator"> - </span>
                    <span className="hebrew-word">
                      {item.acceptedForms?.length > 1
                        ? (item.userAnswer || item.correctForm)
                        : (item.displayCorrectForm || item.correctForm)}
                    </span>
                  </div>
                  <div className={`attempts-info ${item.hadError ? 'multiple-attempts' : ''}`}>
                    {!item.hadError ? (
                      <span>✓ תשובה נכונה</span>
                    ) : (
                      <span>✗ נסיונות: {item.attempts}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="game-end-buttons">
              <button onClick={() => window.location.reload()} className="game-button restart-button">
                שחק מחדש
              </button>
              <button onClick={() => setShowResultsPopup(true)} className="game-button send-button">
                שתף תוצאות
              </button>
            </div>
          </div>

          {showResultsPopup && (
            <div className="popup-overlay" onClick={() => setShowResultsPopup(false)}>
              <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                <button className="popup-close-button" onClick={() => setShowResultsPopup(false)}>
                  ×
                </button>
                <h3>שתף את התוצאות</h3>
                <p>בחר איך תרצה לשתף את התוצאות שלך:</p>
                <div className="popup-buttons">
                  <button onClick={shareViaEmail} className="popup-button email-button">
                    💾 הורד תמונה
                  </button>
                  <button onClick={shareViaWhatsApp} className="popup-button whatsapp-button">
                    📱 שתף בוואטסאפ
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  if (!isLoaded) {
    return <div className="grammatical-form-game loading">טוען משחק...</div>;
  }

  if (loadError) {
    return (
      <div className="App">
        <header className="App-header">
          <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
            <button onClick={handleBackToGames} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
              חזרה למשחקי הפועל ↩
            </button>
            <h1 className="game-title-with-back" style={{ margin: 0 }}>{title}</h1>
          </div>
        </header>
        <main className="game-container results-screen">
          <div className="words-summary">
            <h3>{loadError}</h3>
          </div>
        </main>
      </div>
    );
  }

  if (!currentQuestion) {
    return <div className="grammatical-form-game loading">טוען משחק...</div>;
  }

  const topLocksForms = includeMasdar
    ? (isMobile ? [forms[0], forms[1]] : forms.slice(0, 3))
    : (isMobile ? forms.slice(0, 2) : forms.slice(0, 3));
  const middleLocksForms = isMobile
    ? (includeMasdar ? [forms[2], forms[4]] : [forms[2], forms[3]])
    : null;
  const bottomLocksForms = includeMasdar
    ? (isMobile ? [forms[3], forms[5]] : forms.slice(3, 6))
    : (isMobile ? [forms[4]] : forms.slice(3, 5));
  const mobileLayoutClass = includeMasdar ? 'mobile-six-lock-layout' : 'mobile-five-lock-layout';
  const bottomLockPositionOffset = isMobile ? 4 : 3;

  return (
    <div className={`grammatical-form-game ${mobileLayoutClass}`}>
      <div className="game-header">
        <button onClick={handleBackToGames} className="back-to-verbs-button">
         ↩ חזרה למשחקי הפועל
        </button>
        <h1>{title}</h1>
        <p className="game-instructions-animate">גרור את היד לשעון הנכון</p>
      </div>

      <div className="grammatical-game-board">
        <div className="locks-row top-locks">
          {topLocksForms.map((form, index) => (
            <div
              key={form.id}
              data-form-id={form.id}
              data-lock-name={form.name}
              className={`lock lock-position-${index} ${
                selectedLock === form.id ? (feedback === 'correct' ? 'unlocked' : 'wrong-shake') : ''
              } ${hoveredLock === form.id ? 'hovered' : ''}`}
            >
              <span className="lock-icon" aria-hidden="true">
                <img className="lock-icon-image" src={FORM_ICON} alt="" />
              </span>
              <div className="lock-label">{form.name}</div>
            </div>
          ))}
        </div>

        <div 
          className={`key-container ${draggedKey ? 'dragging' : ''} ${feedback === 'correct' ? 'correct-pulse' : ''}`}
          onPointerDown={handlePointerStart}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          style={{ touchAction: 'none' }}
        >
          <div className="key">
            <div className="key-icon">👈🏻</div>
            <div className="key-verb">{currentQuestion.verb}</div>
          </div>
          <div className="progress">{currentVerbIndex + 1}/{gameVerbs.length}</div>
        </div>

        {middleLocksForms && (
          <div className="locks-row middle-locks">
            {middleLocksForms.map((form, index) => (
              <div
                key={form.id}
                data-form-id={form.id}
                data-lock-name={form.name}
                className={`lock lock-position-${index + 2} ${
                  selectedLock === form.id ? (feedback === 'correct' ? 'unlocked' : 'wrong-shake') : ''
                } ${hoveredLock === form.id ? 'hovered' : ''}`}
              >
                <span className="lock-icon" aria-hidden="true">
                  <img className="lock-icon-image" src={FORM_ICON} alt="" />
                </span>
                <div className="lock-label">{form.name}</div>
              </div>
            ))}
          </div>
        )}

        <div className="locks-row bottom-locks">
          {bottomLocksForms.map((form, index) => (
            <div
              key={form.id}
              data-form-id={form.id}
              data-lock-name={form.name}
              className={`lock lock-position-${index + bottomLockPositionOffset} ${
                selectedLock === form.id ? (feedback === 'correct' ? 'unlocked' : 'wrong-shake') : ''
              } ${hoveredLock === form.id ? 'hovered' : ''}`}
            >
              <span className="lock-icon" aria-hidden="true">
                <img className="lock-icon-image" src={FORM_ICON} alt="" />
              </span>
              <div className="lock-label">{form.name}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default VerbTenseGame2;
